<template>
  <CommonLoading
		v-if="loading"
	/>
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center">
      <!-- Fundo preto semi-transparente -->
      <div class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>
      
      <!-- Conteúdo do Modal -->
      <TransitionRoot as="template" :show="isOpen">
        <div class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-4xl sm:p-6 max-h-[90vh] overflow-y-auto">
          <!-- Título e botão de fechar -->
          <div class="flex justify-between items-start mb-6">
            <h2 class="text-xl font-bold leading-9 tracking-tight text-gray-900">
              {{ checked ? 'Check-in Realizado' : 'Iniciando Visita' }}
            </h2>
            <button 
              class="text-gray-400 hover:text-gray-600 -mt-4" 
              @click="closeModal"
            >
              <span class="sr-only">Fechar</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <!-- Estado "Em trânsito" -->
          <div v-if="visit.status === 'Agendamento presencial' &&
            visit.status_visita === 'Agendamento presencial' &&
            visit.chegou_no_destino === false" class="mt-4 text-center">
            <p class="text-gray-700 animate-pulse text-sm">Em trânsito...</p>
            <p class="mt-2 text-sm text-gray-900">
              Localização atual: {{ currentLocation || 'Buscando...' }}
            </p>

            <div class="mt-4 flex flex-wrap justify-center gap-2">
              <button 
                class="text-sm px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                @click="checkIn"
              >
                Fazer Check-in
              </button>

              <button 
                class="text-sm px-3 py-1 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                @click="pauseLocationTracking(visitId)"
              >
                Pausar Localização
              </button>

              <button 
                class="text-sm px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
                @click="startLocationTracking"
              >
                Retomar Localização
              </button>
            </div>
          </div>
  
          <!-- Formulário após Check-in -->
          <div v-else class="mt-6">
            <p v-if="visit.status === 'Agendamento presencial'" class="text-sm text-gray-900 mb-4">Localização de Check-in: {{ checkInLocation }}</p>
            
            <!-- Observações do Agendamento -->
            <div v-if="visit.observacoes && visit.observacoes.length > 0" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Observações do Agendamento</label>
              <div class="border border-gray-300 rounded-md p-3 bg-gray-50 max-h-32 overflow-y-auto">
                <ul class="space-y-2">
                  <li v-for="(obs, index) in visit.observacoes" :key="index" class="text-sm text-gray-700">
                    <strong>{{ obs.autor_email }}:</strong> {{ obs.descricao }}
                  </li>
                </ul>
              </div>
            </div>

            <form @submit.prevent="submitForm" class="grid grid-cols-2 gap-4">
              <div v-if="visit?.status_visita !== 'finalizada'" class="col-span-2">

                <div class="col-span-2 flex items-center gap-2">
                  <input type="checkbox" v-model="desistencia" class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                  <label class="text-sm text-gray-700">Houve Desistencia?</label>
                </div>
                <!-- Motivo ou Situação -->
                <div v-if="desistencia" class="col-span-2 mt-6">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Motivo de desistencia visita</label>
                  <select
                    v-model="formData.motivo"
                    class="block w-full border border-gray-300 py-2 px-2 rounded-md text-sm"
                  >
                    <option value="">Selecione um motivo</option>
                    <option value="analise">Em análise</option>
                    <option value="ausente">Ausente</option>
                    <option value="reagendado">Reagendado</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>

                <!-- Observação -->
                <div v-if="formData.motivo == 'outro'" class="col-span-2 mt-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    v-model="formData.observacao"
                    rows="3"
                    class="block w-full border border-gray-300 py-2 px-2 rounded-md text-sm"
                    placeholder="Descreva o que ocorreu na visita..."
                  ></textarea>
                </div>
              </div>
              <div v-if="!formData?.motivo && visit?.status_visita !== 'finalizada'" class="col-span-2 grid grid-cols-2 gap-4">
                <!-- Se for Visita Individual ou Em Grupo Temporaria (Proposta Externa) -->
                <div v-if="visit.tipo_visita === 'individual' || visit.tipo_visita === 'em_grupo_temporaria'" class="col-span-2 grid grid-cols-2 gap-4">
                  <div class="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-2">
                    <h3 class="text-sm font-semibold text-blue-800">Fluxo de Visita Individual</h3>
                    <p class="text-xs text-blue-700 mt-1">Esta visita foi agendada como Individual. O preenchimento do questionário médico de saúde e cônjuge é opcional. Você pode anexar o PDF da proposta assinada externamente.</p>
                  </div>

                  <!-- Identificação do Cliente -->
                  <div class="col-span-2 bg-gray-50 p-4 rounded-md border border-gray-200 grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <div class="col-span-2 font-semibold text-xs text-gray-900 border-b pb-1 mb-1">Dados do Proponente</div>
                    <div><strong>Nome:</strong> {{ formData.nome }}</div>
                    <div><strong>CPF:</strong> {{ formData.cpf }}</div>
                    <div><strong>E-mail:</strong> {{ formData.email }}</div>
                    <div><strong>Telefone:</strong> {{ formData.telefone }}</div>
                  </div>

                  <!-- Plano -->
                  <div class="col-span-2 mt-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Plano Escolhido</label>
                    <select
                      @change="selecionarPlano(formData.plan_id)"
                      v-model="formData.plan_id"
                      class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md"
                    >
                      <option value="">Selecione um plano</option>
                      <option v-for="plano in planosDisponiveis" :key="plano.id" :value="plano.id">
                        {{ plano.nome }}
                      </option>
                    </select>
                  </div>

                  <!-- Seleção do Arquivo de Proposta Externa -->
                  <div class="col-span-2 mt-2">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Anexar Proposta Externa (PDF ou Imagem)</label>
                    <input
                      type="file"
                      ref="fileInput"
                      accept="application/pdf,image/*"
                      @change="onFileChange"
                      class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p v-if="propostaArquivo" class="text-xs text-green-600 mt-1">✓ Arquivo selecionado: {{ propostaArquivo.name }}</p>
                  </div>

                  <!-- Alertas de Regras de Venda -->
                  <div v-if="formData.plan_id" class="col-span-2 mt-2">
                     <div v-if="ageLimitWarning" class="p-3 bg-red-50 text-red-700 border border-red-200 rounded text-sm mb-2">
                        {{ ageLimitWarning }}
                     </div>
                     <div v-if="capitalLimitWarning" class="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded text-sm">
                        {{ capitalLimitWarning }}
                     </div>
                     <div v-if="pricingInfo" class="p-3 bg-blue-50 text-blue-800 border border-blue-200 rounded text-sm mt-2">
                        {{ pricingInfo }}
                     </div>
                  </div>

                  <!-- Valores Financeiros -->
                  <div class="col-span-2 grid grid-cols-2 gap-4 border border-gray-100 p-4 rounded-md bg-gray-50 mt-4">
                    <div class="col-span-2 font-semibold text-xs text-gray-700 mb-2 border-b pb-1">Informações Financeiras</div>
                    
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Capital segurado:</label>
                      <input
                        type="number"
                        step="0.01"
                        v-model="formData.capital"
                        placeholder="Ex: 100000"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                      />
                    </div>
                    <div class="pt-6">
                      <button
                        type="button"
                        @click="calcularPremio"
                        class="w-full text-sm px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none"
                      >
                        Calcular Prêmio
                      </button>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Prêmio líquido:</label>
                      <input
                        type="number"
                        step="0.01"
                        v-model="formData.premio_liquido"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Valor do IOF:</label>
                      <input
                        type="number"
                        step="0.01"
                        v-model="formData.valor_iof"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Prêmio total:</label>
                      <input
                        type="number"
                        step="0.01"
                        v-model="formData.premio_total"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Forma de cobrança:</label>
                      <select v-model="formData.forma_pagamento" class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md">
                        <option value="boleto">Boleto Bancário</option>
                        <option value="debito">Débito em Conta</option>
                        <option value="folha">Desconto em Folha</option>
                      </select>
                    </div>

                    <!-- Extra Débito -->
                    <div v-if="formData.forma_pagamento === 'debito'" class="col-span-2 mt-4 grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                        <input type="text" v-model="formData.banco" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="Ex: Banco do Brasil" />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Agência</label>
                        <input type="text" v-model="formData.agencia" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="Número da agência" />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Conta</label>
                        <input type="text" v-model="formData.conta" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="Número da conta" />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">CPF do Titular</label>
                        <input type="text" v-model="formData.cpf_titular" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="CPF do titular" />
                      </div>
                    </div>

                    <!-- Extra Folha -->
                    <div v-if="formData.forma_pagamento === 'folha'" class="col-span-2 mt-4">
                      <label class="block text-sm font-medium text-gray-700 mb-1">Matrícula</label>
                      <input 
                        type="text" 
                        v-model="formData.matricula" 
                        class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" 
                        placeholder="Número de matrícula do funcionário" 
                      />
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Periodicidade de Pagamentos:</label>
                      <select v-model="formData.periodicidade_pagamento" class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md">
                        <option value="Mensal">Mensal</option>
                        <option value="Anual">Anual</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Se for Visita Tradicional (Em Grupo / Outras) -->
                <div v-else class="col-span-2 grid grid-cols-2 gap-4">
                  <!-- Plano -->
                <div class="col-span-2 mt-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Plano Escolhido</label>
                  <select
                    @change="selecionarPlano(formData.plan_id)"
                    v-model="formData.plan_id"
                    class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md"
                  >
                    <option value="">Selecione um plano</option>
                    <option v-for="plano in planosDisponiveis" :key="plano.id" :value="plano.id">
                      {{ plano.nome }}
                    </option>
                  </select>
                </div>

                <!-- Alertas de Regras de Venda (Idade / Capital Limite) -->
                <div v-if="formData.plan_id" class="col-span-2 mt-2">
                   <div v-if="ageLimitWarning" class="p-3 bg-red-50 text-red-700 border border-red-200 rounded text-sm mb-2">
                      {{ ageLimitWarning }}
                   </div>
                   <div v-if="capitalLimitWarning" class="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded text-sm">
                      {{ capitalLimitWarning }}
                   </div>
                   <div v-if="pricingInfo" class="p-3 bg-blue-50 text-blue-800 border border-blue-200 rounded text-sm mt-2">
                      {{ pricingInfo }}
                   </div>
                </div>
                
                <div v-if="formData.plan_id" class="col-span-1 mt-4 flex items-center gap-2 pt-6">
                   <div v-if="planosDisponiveis.find(p => p.id === formData.plan_id)?.permite_dependente" class="flex items-center gap-2">
                      <input type="checkbox" v-model="formData.adicionarConjuge" class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-400" />
                      <label class="text-sm font-medium text-gray-700">Adicionar Cônjuge?</label>
                   </div>
                </div>

                <!-- Divider do Proponente -->
                <div class="col-span-2 mt-10 mb-5">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300"></div>
                    </div>
                    <div class="relative flex justify-center">
                      <span class="bg-white text-center px-2 text-sm text-gray-500 z-10">
                      <strong>DADOS DO PROPONENTE {{ formData.nome }}</strong>
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nome do Proponente</label>
                  <input v-model="formData.nome" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                  <input v-model="formData.cpf" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento</label>
                  <input v-model="formData.data_nascimento" type="date" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nacionalidade</label>
                  <input v-model="formData.proponente_nacionalidade" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">RG</label>
                  <input v-model="formData.rg" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Órgão Expedidor</label>
                  <input v-model="formData.proponente_orgao_emissor" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Data de Expedição</label>
                  <input v-model="formData.proponente_data_expedicao" type="date" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md"/>
                </div>
                <!-- Filhos -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Possuiu filhos?</label>
                  <input v-model="formData.proponente_filhos" type="number" placeholder="Quantidade de filhos" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md" />
                </div>
                <!-- Admissão -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Data de Admissão</label>
                  <input v-model="formData.proponente_admissao" type="date" placeholder="Data de admissão" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Sexo</label>
                  <select v-model="formData.proponente_sexo" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md">
                    <option value="Masculino">Masculino</option>
                    <option value="Feminino">Feminino</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Estado Civil</label>
                  <select v-model="formData.proponente_estado_civil" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md">
                    <option value="S">Solteiro</option>
                    <option value="C">Casado</option>
                    <option value="V">Viúvo</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
                <div class="col-span-1 flex items-center gap-2">
                  <input type="checkbox" v-model="formData.retired" class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                  <label class="text-sm text-gray-700">Aposentado?</label>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Ocupação</label>
                  <input v-model="formData.carreira" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Renda Mensal</label>
                  <input v-model="formData.proponente_salario" type="number" step="0.01" class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"/>
                </div>
                <!-- Divider informçãos de contato e endereço -->
                <div class="col-span-2 mt-10 mb-5">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300"></div>
                    </div>
                    <div class="relative flex justify-center">
                      <span class="bg-white text-center px-2 text-sm text-gray-500 z-10">
                      <strong> INFORMAÇÕES DE CONTATO E ENDEREÇO</strong>
                      </span>
                    </div>
                  </div>
                </div>
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Endereço Residencial</label>
                  <input v-model="formData.proponente_endereco" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>

                <!-- Estado -->
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <input 
                    v-model="formData.proponente_estado" 
                    class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400" 
                    placeholder="UF (ex: PE)" 
                  />
                </div>

                <!-- CEP -->
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <input 
                    v-model="formData.proponente_cep" 
                    class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400" 
                    placeholder="Digite o CEP" 
                  />
                </div>

                <!-- Número -->
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input 
                    v-model="formData.proponente_endereco_numero" 
                    class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400" 
                    placeholder="Número da residência" 
                  />
                </div>

                <!-- Complemento -->
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input 
                    v-model="formData.proponente_endereco_complemento" 
                    class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400" 
                    placeholder="Ex: Apto, Casa, Bloco..." 
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input v-model="formData.proponente_bairro" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input v-model="formData.proponente_cidade" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input v-model="formData.telefone" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                  <input v-model="formData.email" type="email" class="block w-full border-0 border-b border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-0 focus:border-blue-400"/>
                </div>
                <div class="col-span-2 flex items-center gap-2">
                  <input type="checkbox" v-model="formData.email_consent" class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                  <label class="text-sm text-gray-700">Autoriza envio de e-mail?</label>
                </div>
                <!-- Perguntas Form proposta -->
                <div class="col-span-2 mt-10 mb-5">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300"></div>
                    </div>
                    <div class="relative flex justify-center">
                      <span class="bg-white text-center px-2 text-sm text-gray-500 z-10">
                      <strong>  
                        Perguntas Formulário 
                        {{ planosDisponiveis.find(p => p.id === formData.plan_id)?.nome || '' }}
                      </strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div v-for="pergunta in planosDisponiveis.find(p => p.id === formData.plan_id)?.perguntas || []" 
                    :key="pergunta.texto" class="mb-6">

                  <h4 class="font-medium">
                    {{ pergunta.texto }}
                    <span v-if="pergunta.grupo">({{ pergunta.grupo }})</span>
                  </h4>

                  <div v-for="sub in pergunta.subgrupos" :key="sub.nome" class="mb-4">
                    <h5 v-if="sub.nome" class="text-sm text-gray-500">{{ sub.nome }}</h5>

                    <!-- opções -->
                    <div v-for="(opcao, idx) in sub.opcoes" :key="opcao.id" class="mb-2">

                      <!-- checkbox_sim_nao -->
                      <template v-if="opcao.tipo === 'checkbox_sim_nao'">
                        <label>
                          <input
                            type="checkbox"
                            :checked="idx === 0 
                              ? formData.respostas[opcao.id]?.sim === true 
                              : formData.respostas[opcao.id]?.nao === true"
                            @change="toggleResposta(sub, idx)"
                          />
                          {{ idx === 0 ? 'Sim' : 'Não' }}
                        </label>
                      </template>

                      <!-- checkbox_sim -->
                      <template v-else-if="opcao.tipo === 'checkbox_sim'">
                        <label>
                          <input
                            type="checkbox"
                            :checked="formData.respostas[opcao.id]?.sim === true"
                            @change="toggleRespostaSingle(opcao)"
                          />
                          Sim
                        </label>
                      </template>

                      <!-- valor (livre) -->
                      <template v-else-if="opcao.tipo === 'valor'">
                        <input
                          v-if="formData.respostas[opcao.id]"
                          v-model="formData.respostas[opcao.id].valor"
                          type="text"
                          class="w-full border border-gray-300 rounded-md p-2"
                          placeholder="Entre com o valor"
                        />
                      </template>
                      <!-- número -->
                      <template v-else-if="opcao.tipo === 'numero'">
                        <input
                          type="number"
                          v-model="formData.respostas[opcao.id].valor"
                          class="w-full border border-gray-300 rounded-md p-2"
                          placeholder="Digite um número"
                        />
                      </template>

                    </div>
                  </div>
                </div>

                <!-- Informções fiscais -->
                <div class="col-span-2 mt-10 mb-5">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300"></div>
                    </div>
                    <div class="relative flex justify-center">
                      <span class="bg-white text-center px-2 text-sm text-gray-500 z-10">
                      <strong> Informações fiscais </strong>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Capital -->
                <div class="col-span-2 grid grid-cols-2 gap-4 items-end">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Capital segurado:</label>
                    <input
                      type="number"
                      step="0.01"
                      v-model="formData.capital"
                      placeholder="Ex: 100000"
                      class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                    />
                  </div>
                  <div>
                    <button
                      type="button"
                      @click="calcularPremio"
                      class="w-full text-sm px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                    >
                      Calcular Prêmio
                    </button>
                  </div>
                </div>

                <!-- Prêmio líquido -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Prêmio líquido:</label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="formData.premio_liquido"
                    placeholder="Ex: 80.00"
                    class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                  />
                </div>

                <!-- Valor do IOF -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Valor do IOF:</label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="formData.valor_iof"
                    placeholder="Ex: 5.60"
                    class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                  />
                </div>

                <!-- Prêmio total -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Prêmio total:</label>
                  <input
                    type="number"
                    step="0.01"
                    v-model="formData.premio_total"
                    placeholder="Ex: 85.60"
                    class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md"
                  />
                </div>

                <!-- Forma de cobrança -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Forma de cobrança:</label>
                  <select v-model="formData.forma_pagamento" class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md">
                    <option value="boleto">Boleto Bancário</option>
                    <option value="debito">Débito em Conta</option>
                    <option value="folha">Desconto em Folha</option>
                  </select>
                </div>

                <!-- Form extra se for Débito em Conta -->
                <div v-if="formData.forma_pagamento === 'debito'" class="col-span-2 mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                    <input type="text" v-model="formData.banco" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="Ex: Banco do Brasil" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Agência</label>
                    <input type="text" v-model="formData.agencia" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="Número da agência" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Conta</label>
                    <input type="text" v-model="formData.conta" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="Número da conta" />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">CPF do Titular</label>
                    <input type="text" v-model="formData.cpf_titular" class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" placeholder="CPF do titular" />
                  </div>
                </div>

                <!-- Form extra se for Desconto em Folha -->
                <div v-if="formData.forma_pagamento === 'folha'" class="col-span-2 mt-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Matrícula</label>
                  <input 
                    type="text" 
                    v-model="formData.matricula" 
                    class="block w-full border border-gray-300 rounded-md py-2 px-2 text-sm" 
                    placeholder="Número de matrícula do funcionário" 
                  />
                </div>

                <!-- Periodicidade de Pagamentos -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Periodicidade de Pagamentos:</label>
                  <select v-model="formData.periodicidade_pagamento" class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md">
                    <option value="Mensal">Mensal</option>
                    <option value="Anual">Anual</option>
                  </select>
                </div>

                <!-- conjuge -->
                <div v-if="formData.adicionarConjuge && planosDisponiveis.find(p => p.id === formData.plan_id)?.permite_dependente" class="col-span-2 mt-10 mb-5">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300"></div>
                    </div>
                    <div class="relative flex justify-center">
                      <span class="bg-white text-center px-2 text-sm text-gray-500 z-10">
                      <strong> CONJUGE </strong>
                      </span>
                    </div>
                  </div>
                </div>
                <!-- <div class="col-span-2 flex items-center gap-2">
                  <input type="checkbox" v-model="formData.adicionarConjuge" class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                  <label class="text-sm text-gray-700">Adicionar Conjuge?</label>
                </div> -->
                <div v-if="formData.adicionarConjuge == true && planosDisponiveis.find(p => p.id === formData.plan_id)?.permite_dependente" class="col-span-2">
                  
                  <!-- Formulário de conjuge -->
                  <div class="grid grid-cols-2 gap-4 border border-blue-200 p-4 rounded-md shadow-sm mt-6 col-span-2">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                      <input
                        v-model="formData.conjuge.conjuge_nome"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                      <input
                        v-model="formData.conjuge.cpf"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento</label>
                      <input v-model="formData.conjuge.conjuge_data_nascimento" type="date" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md"/>
                    </div>
                    
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">RG</label>
                      <input
                        v-model="formData.conjuge.conjuge_rg"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Data de expedição do documento</label>
                      <input v-model="formData.conjuge.conjuge_data_expedicao" type="date" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md"/>
                    </div>
                     <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Orgão Emissor</label>
                      <input
                        v-model="formData.conjuge.conjuge_orgao_emissor"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                      />
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">DDD</label>
                      <input
                        type="number"
                        v-model="formData.conjuge.conjuge_ddd"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                      <input
                        v-model="formData.conjuge.conjuge_telefone"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Nacionalidade</label>
                      <input
                        v-model="formData.conjuge.conjuge_nacionalidade"
                        class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Sexo</label>
                      <select v-model="formData.conjuge.conjuge_sexo" class="block w-full border border-gray-300 py-2 pl-2 pr-2 text-sm text-gray-900 focus:ring-1 focus:ring-blue-400 rounded-md">
                        <option value="Masculino">Masculino</option>
                        <option value="Feminino">Feminino</option>
                      </select>
                    </div>
                    <div class="col-span-2 flex items-center gap-2">
                      <input type="checkbox" v-model="formData.conjuge_political_exposed" class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-400" />
                      <label class="text-sm text-gray-700">Conjuge é pessoa politicamente exposta?</label>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Peso (kg)</label>
                      <input v-model="formData.conjuge.conjuge_peso" type="number" step="0.1" class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md" />
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Altura (m)</label>
                      <input v-model="formData.conjuge.conjuge_altura" type="number" step="0.01" class="block w-full border border-gray-300 py-2 px-2 text-sm rounded-md" />
                    </div>
                  </div>
                </div>
                <!-- beneficiários -->
                <div class="col-span-2 mt-10 mb-5">
                  <div class="relative">
                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                      <div class="w-full border-t border-gray-300"></div>
                    </div>
                    <div class="relative flex justify-center">
                      <span class="bg-white text-center px-2 text-sm text-gray-500 z-10">
                      <strong> BENEFICIÁRIOS </strong>
                      </span>
                    </div>
                  </div>
                </div>
                <!-- Formulário de Novo Beneficiário -->
                <div class="grid grid-cols-2 gap-4 border border-blue-200 p-4 rounded-md shadow-sm mt-6 col-span-2">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Nome do Beneficiário</label>
                    <input
                      v-model="novoBeneficiario.nome"
                      class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                    <input
                      v-model="novoBeneficiario.cpf"
                      class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Percentual</label>
                    <input
                      type="number"
                      v-model="novoBeneficiario.percentual"
                      class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Grau de Parentesco
                    </label>

                    <select
                      v-model="novoBeneficiario.grauParentesco"
                      class="block w-full border border-gray-300 py-2 px-2 text-sm text-gray-900 rounded-md"
                    >
                      <option value="" disabled>Selecione o grau de parentesco</option>

                      <option value="conjuge">Cônjuge</option>
                      <option value="esposa">Esposa</option>
                      <option value="marido">Marido</option>

                      <option value="filho">Filho</option>
                      <option value="filha">Filha</option>

                      <option value="mae">Mãe</option>
                      <option value="pai">Pai</option>

                      <option value="irmao">Irmão</option>
                      <option value="irma">Irmã</option>

                      <option value="avo">Avô</option>
                      <option value="avoh">Avó</option>

                      <option value="neto">Neto</option>
                      <option value="neta">Neta</option>

                      <option value="tio">Tio</option>
                      <option value="tia">Tia</option>

                      <option value="sobrinho">Sobrinho</option>
                      <option value="sobrinha">Sobrinha</option>

                      <option value="primo">Primo</option>
                      <option value="prima">Prima</option>

                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  <div class="col-span-2 flex justify-end">
                    <button
                      @click.prevent="adicionarBeneficiario"
                      class="text-sm px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Adicionar Beneficiário
                    </button>
                  </div>
                </div>
                <div class="col-span-2 border-t pt-4">
                  <h3 class="text-sm font-semibold text-gray-800 mb-2">Beneficiarios da proposta</h3>
                  <ul class="space-y-2">
                    <li
                      v-for="(beneficiario, index) in formData.beneficiarios"
                      :key="index"
                      class="flex justify-between items-center bg-gray-100 px-3 py-2 rounded-md"
                    >
                      <div class="text-sm text-gray-700">
                        <p><strong>Nome:</strong> {{ beneficiario.nome }}</p>
                        <p><strong>CPF:</strong> {{ beneficiario.cpf }}</p>
                        <p><strong>Percentual:</strong> {{ beneficiario.percentual }}</p>
                        <p><strong>Grau de Parentesco:</strong> {{ beneficiario.grauParentesco }}</p>
                      </div>

                      <button
                        @click.prevent="removerBeneficiario(index)"
                        class="text-red-600 text-xs underline hover:text-red-800"
                      >
                        Remover
                      </button>
                    </li>
                  </ul>
                </div>
                </div>
              </div>

              <!-- ❌ Visita já finalizada -->
              <div v-if="visit?.status_visita === 'finalizada'" class="col-span-2 p-4 bg-red-50 border border-red-300 rounded-md text-sm text-red-800">
                Esta visita já foi finalizada. Se a proposta foi enviada, acesse a aba de propostas para acompanhar o andamento da análise financeira.
              </div>

              <!-- ⚠️ Motivo de cancelamento escolhido -->
              <div  v-if="formData.motivo" class="col-span-2 p-4 bg-yellow-50 border border-yellow-300 rounded-md text-sm text-yellow-800">
                Você selecionou um motivo de desistência. Os dados do segurado não serão exibidos enquanto essa opção estiver preenchida.
              </div>
              <!-- Aviso de status encerrado -->
              <div v-if="visit.status_visita !== 'em andamento'" class="col-span-2 mt-6">
                <div class="w-full bg-yellow-100 text-yellow-800 px-4 py-3 rounded-md border border-yellow-300 shadow-sm text-sm text-center">
                  Visita está em <strong>{{ visit.status_visita }}</strong>
                </div>
              </div>
              <!-- Botão de Envio (somente se estiver em andamento) -->
              <div v-if="visit.status_visita === 'em andamento' || visit.status_visita === 'Agendamento remoto'" class="col-span-2 mt-6">
                <button
                  type="submit"
                  class="w-full text-sm px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  Enviar Formulário
                </button>
              </div>
            </form>
          </div>
        </div>
      </TransitionRoot>
    </div>
  </template>
  
 <script>

import { TransitionRoot } from '@headlessui/vue';
import { pageVariables } from '@/views/insideviews/visitas/PageVariablesVisitas';
import VisitasRepository from '@/services/VisitasRepository.vue';
import { handleUnauthorized } from '@/services/AuthSer/authServiceFunction';
import LocationRepository from '@/services/LocationRepository.vue';
import ListaDeAgendamentosRepository from '@/services/ListaDeAgendamentosRepository.vue';
import CommonLoading from '@/components/generalcomponents/CommonLoading';
import Swal from "sweetalert2";

export default {
  name: 'VisitModal',
  emits:['form-send'],
  props: {
    visitId: { type: Number, required: true },
    id: { type: Number, required: true },
    visitaDependente: { type: Boolean, required: true },
    checked: { type: Boolean, required: true },
    visit: { type: Object, required: true },
  },
  created() {
    console.log(this.visit)
  },
  data() {
    return {
      ...pageVariables,
      isOpen: false,
      desistencia:false,
      onlineUser: JSON.parse(window.localStorage.getItem('myUser')),
      checkedIn: this.checked,
      currentLocation: null,
      checkInLocation: null,
      loading: false,
      formData: {
        segurado_id:'',
        dependente_id:'',
        nome: '',
        cpf: '',
        data_nascimento: '',
        sports: false,
        sports_details:'',
        family_history:false,
        family_history_details:'',
        proponente_nacionalidade: '',
        rg: '',
        proponente_orgao_emissor: '',
        proponente_data_expedicao: '',
        proponente_sexo: '',
        proponente_estado_civil: '',
        proponente_filhos:'',
        proponente_admissao:'',
        carreira: '',
        periodicidade_pagamento:'',
        proponente_salario: '',
        proponente_endereco: '',
        proponente_bairro: '',
        proponente_cidade: '',
        telefone: '',
        email: '',
        working:'',
        tipo_custeio:'',
        premio_liquido:'',
        valor_iof:'',
        premio_total:'',
        forma_pagamento:'',
        capital:'',
        email_consent: false,
        retired: false,
        peso: null,
        altura: null,
        beneficiarios: [],
        plan_id: '',
        medical_leave: false,
        diseases: [],
        disease_details:'',
        adicionarConjuge:false,
        questoesAdicionais: false,
        propostaArquivo: null,
        conjuge:{
          conjuge_nome:'',
          conjuge_data_nascimento:'',
          conjuge_ddd: '',
          conjuge_telefone: '',
          conjuge_rg: '',
          conjuge_nacionalidade:'',
          conjuge_sexo:'',
          conjuge_political_exposed: false,
          conjuge_data_expedicao:'',
          conjuge_orgao_emissor:'',
          conjuge_peso: '',
          conjuge_altura:''
        },
        respostas: []
      },
      perguntasFixas:[],
      planosDisponiveis: [],
      respostasIds: [],
      novoBeneficiario: {
        nome: '',
        cpf: '',
        percentual: '',
        grauParentesco: ''
      },
      ageLimitWarning: '',
      capitalLimitWarning: '',
      pricingInfo: ''
    }
  },
  beforeUnmount() {
    if (this.locationInterval) {
      clearInterval(this.locationInterval);
    }
  },
  watch: {
    'formData.plan_id': function() {
      this.recalculateSaleRules();
    },
    'formData.capital': function() {
      this.recalculateSaleRules();
    },
    'formData.data_nascimento': function() {
      this.recalculateSaleRules();
    },
    'formData.adicionarConjuge': function() {
      this.recalculateSaleRules();
    },
    'formData.conjuge.conjuge_data_nascimento': function() {
      this.recalculateSaleRules();
    }
  },
  methods: {
    async openModal() {
      this.isOpen = true;
      this.currentLocation = null;
      this.checkInLocation = null;
      if (this.visit.status_visita != 'finalizada') {
        await this.fetchSeguradoData();
        await  this.getPlanosDisponiveis();
      }
      const chegouNoDestino = this.visit.chegou_no_destino === true;
      const statusDiferenteDePresencial = this.visit.status !== 'Agendamento presencial';
      const statusNaoAndamento = this.visit.status_visita !== 'Agendamento presencial';

      // Se QUALQUER UM for verdadeiro, bloqueia
      if (chegouNoDestino || statusDiferenteDePresencial || statusNaoAndamento) {
        console.log('🔒 Bloqueado - não chamando funções');
      } else {
        console.log('✅ Chamando funções...');
        this.startLocationTracking();
        this.changeVisitStatus();
      }
    },

    closeModal() {
      this.isOpen = false;
      clearInterval(this.locationInterval);
    },

    selecionarPlano(planoId) {
  const plano = this.planosDisponiveis.find(p => p.id === planoId);
  if (!plano) return;

  this.formData.respostas = {};

  plano.perguntas.forEach(pergunta => {
    pergunta.subgrupos.forEach(sub => {
      sub.opcoes.forEach(op => {
        this.formData.respostas[op.id] = {
          proposta_id: this.formData.proposta_id,
          modelo_proposta_depara_id: op.id,
          valor: "", // 🔥 sempre existe
          sim: false,
          nao: false
        };
      });
    });
  });

  console.log(this.formData.respostas);
  this.recalculateSaleRules();
},

    getPerguntasAgrupadas() {
  const plano = this.planosDisponiveis.find(p => p.id === this.formData.plan_id);
  if (!plano) return [];

  const grupos = [];

  plano.perguntas.forEach(p => {
    const chave = `${p.pergunta}_${p.subgrupo || 'default'}`;

    let grupo = grupos.find(g => g.chave === chave);
    if (!grupo) {
      grupo = {
        chave,
        texto: p.pergunta,
        subgrupo: p.subgrupo || null,
        sim: null,
        nao: null,
        tipo: p.tipo,
        opcoes: p.subgrupos?.flatMap(s => s.opcoes) || []
      };
      grupos.push(grupo);
    }

    // identifica sim/não só para checkbox
    grupo.opcoes.forEach(op => {
      if (op.tipo === 'checkbox_sim' || op.tipo === 'checkbox_sim_nao') {
        if (op.nome?.toLowerCase().includes('sim')) {
          grupo.sim = op;
        } else if (op.nome?.toLowerCase().includes('não')) {
          grupo.nao = op;
        }
      }
    });
  });

  return grupos;
},

    toggleResposta(sub, idx) {
      const opcoes = sub.opcoes;

      opcoes.forEach((op, i) => {
        if (!this.formData.respostas[op.id]) {
          this.formData.respostas[op.id] = {
            proposta_id: this.formData.proposta_id,
            modelo_proposta_depara_id: op.id,
            valor: "",
            sim: false,
            nao: false
          };
        }

        this.formData.respostas[op.id].sim = (i === 0 && idx === 0);
        this.formData.respostas[op.id].nao = (i === 1 && idx === 1);
      });
      console.log(this.formData.respostas)
    },

    toggleRespostaSingle(opcao) {
      if (!this.formData.respostas[opcao.id]) {
        this.formData.respostas[opcao.id] = {
          proposta_id: this.formData.proposta_id,
          modelo_proposta_depara_id: opcao.id,
          valor: "",
          sim: false,
          nao: false
        };
      }
      // inverte sim (um único checkbox)
      this.formData.respostas[opcao.id].sim = !this.formData.respostas[opcao.id].sim;
    },

    recalculateSaleRules() {
      this.ageLimitWarning = '';
      this.capitalLimitWarning = '';
      this.pricingInfo = '';
      
      if (!this.formData.plan_id) return;

      const plano = this.planosDisponiveis.find(p => p.id === this.formData.plan_id);
      if (!plano) return;

      const calculateAge = (birthDateStr) => {
        if (!birthDateStr) return { months: 0, years: 0 };
        const birthDate = new Date(birthDateStr);
        const today = new Date();
        let months = (today.getFullYear() - birthDate.getFullYear()) * 12 + (today.getMonth() - birthDate.getMonth());
        return { months, years: Math.floor(months / 12) };
      };

      const findMatriz = (years) => {
        const matrizes = plano.matrizes_reenquadramento || [];
        return matrizes.find(m => years >= m.idade_min_meses && years <= m.idade_max_meses);
      };

      // 1. Titular
      const ageTitular = calculateAge(this.formData.data_nascimento);
      const matrizTitular = findMatriz(ageTitular.years);
      
      if (ageTitular.years > 0 && plano.idade_maxima_ingresso_titular && ageTitular.years > plano.idade_maxima_ingresso_titular) {
        this.ageLimitWarning = `Atenção: A idade do proponente (${ageTitular.years} anos) excede o limite do plano (${plano.idade_maxima_ingresso_titular} anos).`;
      }

      const capitalNumber = parseFloat(this.formData.capital) || 0;

      if (matrizTitular) {
        const capMin = parseFloat(matrizTitular.capital_min);
        const capMax = parseFloat(matrizTitular.capital_max);
        
        this.pricingInfo = `Titular (${ageTitular.years} anos): Capital R$ ${capMin.toLocaleString()} - R$ ${capMax.toLocaleString()}.`;

        if (capitalNumber > 0) {
          if (capitalNumber < capMin || capitalNumber > capMax) {
            this.capitalLimitWarning = `Capital R$ ${capitalNumber.toLocaleString()} fora dos limites do Titular!`;
          }
        }
      } else if (ageTitular.years > 0) {
        this.pricingInfo = `Não há tabela para a idade do Titular (${ageTitular.years} anos).`;
      }

      // 2. Cônjuge
      if (this.formData.adicionarConjuge && plano.permite_dependente) {
        const ageConjuge = calculateAge(this.formData.conjuge.conjuge_data_nascimento);
        const matrizConjuge = findMatriz(ageConjuge.years);

        if (matrizConjuge) {
          this.pricingInfo += ` | Cônjuge (${ageConjuge.years} anos) incluso.`;
        } else if (ageConjuge.years > 0) {
          this.pricingInfo += ` | Cônjuge (${ageConjuge.years} anos) SEM TABELA.`;
        }
      }
    },

    calcularPremio() {
      if (!this.formData.plan_id) return;

      const plano = this.planosDisponiveis.find(p => p.id === this.formData.plan_id);
      if (!plano) return;

      const calculateAge = (birthDateStr) => {
        if (!birthDateStr) return { months: 0, years: 0 };
        const birthDate = new Date(birthDateStr);
        const today = new Date();
        let months = (today.getFullYear() - birthDate.getFullYear()) * 12 + (today.getMonth() - birthDate.getMonth());
        return { months, years: Math.floor(months / 12) };
      };

      const findMatriz = (years) => {
        const matrizes = plano.matrizes_reenquadramento || [];
        return matrizes.find(m => years >= m.idade_min_meses && years <= m.idade_max_meses);
      };

      const capitalNumber = parseFloat(this.formData.capital) || 0;
      let totalPremioLiquido = 0;

      const getPremio = (base, matriz) => {
        if (!matriz) return 0;
        const v = parseFloat(matriz.valor_reajuste) || 0;
        const tipo = matriz.tipo_reajuste;

        if (tipo === 'percentual') {
          // Espelhando o back: round($base * (1 + ($valor / 1000)), 2)
          // O prêmio (acréscimo) é: base * (valor / 1000)
          return base * (v / 1000);
        } else if (tipo === 'fator') {
          return base * v;
        } else if (tipo === 'fixo_monetario') {
          return v;
        }
        return 0;
      };

      // 1. Titular
      const ageTitular = calculateAge(this.formData.data_nascimento);
      const matrizTitular = findMatriz(ageTitular.years);
      totalPremioLiquido += getPremio(capitalNumber, matrizTitular);

      // 2. Cônjuge
      if (this.formData.adicionarConjuge && plano.permite_dependente) {
        const ageConjuge = calculateAge(this.formData.conjuge.conjuge_data_nascimento);
        const matrizConjuge = findMatriz(ageConjuge.years);
        totalPremioLiquido += getPremio(capitalNumber, matrizConjuge);
      }

      // Atualiza valores finais
      this.formData.premio_liquido = totalPremioLiquido.toFixed(2);
      let iofCalc = plano.possui_iof ? totalPremioLiquido * 0.0038 : 0;
      this.formData.valor_iof = iofCalc.toFixed(2);
      this.formData.premio_total = (totalPremioLiquido + iofCalc).toFixed(2);
    },

    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.propostaArquivo = files[0];
    },

    async checkIn() {
      if (!navigator.geolocation) {
        alert('Geolocalização não suportada pelo navegador.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            ficha_visita_id: this.visitId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: new Date().toISOString(),
          };
          this.currentLocation = `${location.latitude}, ${location.longitude}`;
          this.checkInLocation = this.currentLocation;
          await this.sendLocation(location);
          this.startLocationTracking();
        },
        (error) => {
          console.error('Erro ao obter localização:', error);
          this.currentLocation = 'Erro ao buscar localização';
          alert('Erro ao obter localização.');
        }
      );
      this.alterVisitStatus(this.pageCheckInLocation,this.visitId);
      this.checkedIn = true;
    },
    startLocationTracking() {
      this.locationInterval = setInterval(() => {
        this.captureLocation();
      }, 10000);
    },
     pauseLocationTracking(id) {
      if (this.locationInterval) {
        clearInterval(this.locationInterval);
        this.locationInterval = null;
        console.log('Localização pausada');
        this.alterVisitStatus(this.pagePauseLocation,id)
      }
    },
    async captureLocation() {
      if (!navigator.geolocation) {
        console.warn('Geolocalização não suportada.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            ficha_visita_id: this.visitId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            timestamp: new Date().toISOString(),
          };
          this.currentLocation = `${location.latitude}, ${location.longitude}`;
          await this.sendLocation(location);
        },
        (error) => {
          console.error('Erro ao capturar localização:', error);
        }
      );
    },
    async sendLocation(location) {
      const locationRepo = new LocationRepository();
      try {
        const response = await locationRepo.sendLocation(
          this.pageStoreLocation,
          location,
          this.onlineUser.token
        );
        if (response?.status === 201) {
          console.log('Localização enviada com sucesso');
        } else {
          this.error = response?.message;
        }
      } catch (error) {
        handleUnauthorized(error);
      }
    },
    async fetchSeguradoData() {
      this.loading = true
      console.log(this.formData)
      const visitaRepo = new VisitasRepository();
      try {
        const response = await visitaRepo.getSeguradoData(
          this.pageStoreProposta,
          this.onlineUser.token,
          this.id,
          this.visitaDependente
        );
        console.log(response.status)
        if(response?.status === 200){
          this.loading = false
          this.formData.nome = response.data.mainProponent.nome;
          this.formData.cpf = response.data.mainProponent.cpf;
          this.formData.data_nascimento = response.data.mainProponent.data_nascimento;
          this.formData.rg = response.data.mainProponent.rg;
          this.formData.carreira = response.data.mainProponent.carreira;
          this.formData.proponente_endereco = response.data.mainProponent.proponente_endereco;
          this.formData.proponente_bairro = response.data.mainProponent.proponente_bairro;
          this.formData.proponente_cidade = response.data.mainProponent.proponente_cidade;
          this.formData.proponente_estado = response.data.mainProponent.proponente_estado;
          this.formData.proponente_cep = response.data.mainProponent.proponente_cep;
          this.formData.proponente_endereco_numero = response.data.mainProponent.proponente_endereco_numero;
          this.formData.proponente_endereco_complemento = response.data.mainProponent.proponente_endereco_complemento;
          this.formData.telefone = response.data.mainProponent.telefone;
          this.formData.email = response.data.mainProponent.email;
          this.formData.dependentes = response.data.dependentes;
        }else{
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        console.error('Erro ao buscar dados do segurado:', error);
      }
    },

    async getPlanosDisponiveis(){
      this.loading = true;
			const listaRepo = new ListaDeAgendamentosRepository();
      console.log(this.visitId)
			try {
				const response = await listaRepo.getPlanosDisponiveis(this.visitId,this.onlineUser.token);
				if (response?.status === 200) {
          this.loading = false;
					this.planosDisponiveis = response.data
				} else {
          this.loading = false;
					this.error = response?.message;
				}
			} catch (error) {
        this.loading = false;
				handleUnauthorized(error);
			}	
		},

    async submitForm() {
      let segurado_id = null;
      let dependente_id = null;

      if (this.visitaDependente) {
        dependente_id = this.id;
      } else {
        segurado_id = this.id;
      }

      this.loading = true;
      const visitaRepo = new VisitasRepository();

      let payload;
      if (this.visit.tipo_visita === 'individual' || this.visit.tipo_visita === 'em_grupo_temporaria') {
        payload = new FormData();
        payload.append('ficha_visita_id', this.visitId);
        if (segurado_id) payload.append('segurado_id', segurado_id);
        if (dependente_id) payload.append('dependente_id', dependente_id);
        payload.append('plan_id', this.formData.plan_id || '');
        payload.append('capital', this.formData.capital || '');
        payload.append('premio_liquido', this.formData.premio_liquido || '');
        payload.append('premio_total', this.formData.premio_total || '');
        payload.append('valor_iof', this.formData.valor_iof || '');
        payload.append('forma_pagamento', this.formData.forma_pagamento || '');
        payload.append('forma_cobranca', this.formData.forma_pagamento || '');
        payload.append('externa', '1');

        if (this.propostaArquivo) {
          payload.append('arquivo', this.propostaArquivo);
        }
      } else {
        payload = {
          ...this.formData,
          ficha_visita_id: this.visitId,
          segurado_id,
          dependente_id,
        };
      }

      try {
        const response = await visitaRepo.createProposta(
          this.pageStorePropostaCreate,
          payload,
          this.onlineUser.token
        );
        if (response?.status === 200 || response?.status === 201) {
          this.loading = false;
          this.closeModal();
          this.mostrarAlerta();
          this.$emit('form-send', this.visit.id);
        } else {
          this.loading = false;
          this.error = response?.message;
          this.mostrarAlertaErro(this.error);
        }
      } catch (error) {
        this.loading = false;
        handleUnauthorized(error);
        this.mostrarAlertaErro(error);
      }
    },
    async changeVisitStatus(){
			const listaRepo = new VisitasRepository();
			try {
				const response = await listaRepo.updateStatus(this.updateStatus, this.onlineUser.token,this.visitId);
				if (response?.status === 200) {
          console.log(response)
				} else {
					this.error = response?.message;
				}
			} catch (error) {
				handleUnauthorized(error);
			}	
		},
    async alterVisitStatus(page, id) {
      const locationRepo = new LocationRepository();

      // Verifica se a localização atual está definida
      if (!this.currentLocation || !this.currentLocation.includes(',')) {
        this.error = 'Localização ainda não disponível. Aguarde o GPS carregar.';
        console.warn(this.error);
        return;
      }

      const [lat, lng] = this.currentLocation.split(',').map(s => s.trim());

      // Verifica se os valores estão válidos
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
        this.error = 'Coordenadas inválidas. Verifique se o GPS foi ativado corretamente.';
        console.warn(this.error);
        return;
      }

      let actualLocation = {
        latitude: lat,
        longitude: lng
      };

      try {
        const response = await locationRepo.alterVisitStatus(
          page,
          this.onlineUser.token,
          id,
          actualLocation
        );
        if (response?.status === 201) {
          console.log('Ação executada com sucesso');
        } else {
          this.error = response?.message;
        }
      } catch (error) {
        handleUnauthorized(error);
        console.error('Erro ao enviar localização:', error);
      }
    },
    adicionarBeneficiario() {
      const novo = { ...this.novoBeneficiario };
      if (!novo.nome) {
        return;
      }

      this.formData.beneficiarios.push(novo);
      this.novoBeneficiario = {
        nome: '',
        cpf: '',
        percentual: '',
        grauParentesco: ''
      };
    },

    removerBeneficiario(index) {
      this.formData.beneficiarios.splice(index, 1);
    },

    mostrarAlerta() {
			Swal.fire({
				icon: 'success',
				title: 'Sucesso!',
				text: 'A operação foi concluída com sucesso.',
				confirmButtonColor: '#3085d6',
			});
		},

		mostrarAlertaErro(message) {
			Swal.fire({
				icon: 'error',
				title: 'Erro!',
				text: message,
				confirmButtonColor: '#3085d6', 
			});
		},
  },
  components: {
    TransitionRoot,
    CommonLoading,
  },
};
</script>